import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { companiesApi, censusApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft,
  Check,
  UserPlus,
  UserMinus,
  Users,
  ArrowRight,
  X,
  MapPin,
  History,
  Calendar,
  Trash2,
  Download,
  Wrench,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { RemapDialog } from '@/components/RemapDialog';

const STEPS = [
  { id: 1, title: 'Upload File', description: 'Select census file' },
  { id: 2, title: 'Map Columns', description: 'Match fields' },
  { id: 3, title: 'Review Changes', description: 'Confirm updates' },
  { id: 4, title: 'Complete', description: 'Apply changes' },
];

const ALL_FIELDS = ['employee_id', 'first_name', 'last_name', 'start_date', 'address', 'address_2', 'city', 'state', 'zip_code', 'homeowner', 'salary', 'date_of_birth', 'email', 'phone'];

export default function CensusUploadPage() {
  const [step, setStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyOptions, setApplyOptions] = useState({ addNew: true, markQuit: true, updateAddresses: true });
  const [isInitialUpload, setIsInitialUpload] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [remapUploadId, setRemapUploadId] = useState(null);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [processingUploadId, setProcessingUploadId] = useState(null);

  useEffect(() => {
    loadCompanies();
    loadPendingUploads();
  }, []);

  const loadPendingUploads = async () => {
    try {
      const response = await api.get('/census/pending-uploads');
      setPendingUploads(response.data);
    } catch (error) {
      console.error('Failed to load pending uploads:', error);
    }
  };

  const handleProcessPendingUpload = async (record) => {
    try {
      const response = await api.get(`/census/download/${record.id}`, { responseType: 'blob' });
      const loadedFile = new File([response.data], record.file_name, { type: record.file_type || 'application/octet-stream' });
      setSelectedCompany(record.company_id);
      setFile(loadedFile);
      setProcessingUploadId(record.id);
      setStep(1);
      toast.success(`Loaded "${record.file_name}" from ${record.company_name}. Click Continue to map columns.`);
    } catch (error) {
      toast.error('Failed to load the uploaded file');
    }
  };

  const markUploadProcessed = async () => {
    if (!processingUploadId) return;
    try {
      await api.post(`/census/upload/${processingUploadId}/mark-processed`);
      setProcessingUploadId(null);
      loadPendingUploads();
    } catch (error) {
      console.error('Failed to mark upload processed:', error);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      loadUploadHistory(selectedCompany);
      checkIfInitialUpload(selectedCompany);
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companiesApi.getAll();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load companies');
    }
  };

  const loadUploadHistory = async (companyId) => {
    try {
      const response = await api.get(`/census/history/${companyId}`);
      setUploadHistory(response.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const checkIfInitialUpload = async (companyId) => {
    try {
      const response = await api.get('/employees', { params: { company_id: companyId } });
      setIsInitialUpload(response.data.length === 0);
    } catch (error) {
      setIsInitialUpload(true);
    }
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.match(/\.(csv|xlsx|xls)$/i)) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setFile(droppedFile);
    }
  }, []);

  const handlePreviewFile = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const response = await censusApi.preview(file);
      setPreview(response.data);
      
      // Auto-map columns
      const autoMapping = {};
      const columns = response.data.columns.map(c => c.toLowerCase());
      
      // Map first_name
      const firstNameIdx = columns.findIndex(c => c.includes('first'));
      if (firstNameIdx !== -1) autoMapping.first_name = response.data.columns[firstNameIdx];
      
      // Map last_name
      const lastNameIdx = columns.findIndex(c => c.includes('last'));
      if (lastNameIdx !== -1) autoMapping.last_name = response.data.columns[lastNameIdx];
      
      // Map start_date
      const dateIdx = columns.findIndex(c => c.includes('date') || c.includes('hire') || c.includes('start'));
      if (dateIdx !== -1) autoMapping.start_date = response.data.columns[dateIdx];
      
      // Map address (Address 1)
      const addressIdx = columns.findIndex(c => c === 'address 1' || c === 'address1' || (c === 'address' && !columns.some(col => col === 'address 1')));
      if (addressIdx !== -1) autoMapping.address = response.data.columns[addressIdx];
      
      // Map address_2 (Address 2)
      const address2Idx = columns.findIndex(c => c === 'address 2' || c === 'address2' || c.includes('apt') || c.includes('suite') || c.includes('unit'));
      if (address2Idx !== -1) autoMapping.address_2 = response.data.columns[address2Idx];
      
      // Map city
      const cityIdx = columns.findIndex(c => c === 'city');
      if (cityIdx !== -1) autoMapping.city = response.data.columns[cityIdx];
      
      // Map state
      const stateIdx = columns.findIndex(c => c === 'state');
      if (stateIdx !== -1) autoMapping.state = response.data.columns[stateIdx];
      
      // Map zip
      const zipIdx = columns.findIndex(c => c.includes('zip'));
      if (zipIdx !== -1) autoMapping.zip_code = response.data.columns[zipIdx];
      
      // Map homeowner
      const homeownerIdx = columns.findIndex(c => c.includes('homeowner') || c.includes('owner'));
      if (homeownerIdx !== -1) autoMapping.homeowner = response.data.columns[homeownerIdx];
      
      // Map salary
      const salaryIdx = columns.findIndex(c => c.includes('salary') || c.includes('pay') || c.includes('wage') || c.includes('compensation'));
      if (salaryIdx !== -1) autoMapping.salary = response.data.columns[salaryIdx];
      
      // Map date_of_birth
      const dobIdx = columns.findIndex(c => c.includes('birth') || c === 'dob' || c.includes('date of birth'));
      if (dobIdx !== -1) autoMapping.date_of_birth = response.data.columns[dobIdx];
      
      // Map email
      const emailIdx = columns.findIndex(c => c.includes('email') || c.includes('e-mail'));
      if (emailIdx !== -1) autoMapping.email = response.data.columns[emailIdx];
      
      // Map phone
      const phoneIdx = columns.findIndex(c => c.includes('phone') || c.includes('mobile') || c.includes('cell') || c.includes('telephone'));
      if (phoneIdx !== -1) autoMapping.phone = response.data.columns[phoneIdx];
      
      // Map employee_id
      const empIdIdx = columns.findIndex(c => c === 'employee id' || c === 'emp_id' || c === 'employee_id' || c === 'emp id' || c.includes('badge'));
      if (empIdIdx !== -1) autoMapping.employee_id = response.data.columns[empIdIdx];
      
      setColumnMapping(autoMapping);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to read file');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    setLoading(true);
    try {
      if (isInitialUpload) {
        // For initial upload, use the censusApi helper
        const uploadResponse = await censusApi.initialUpload(file, selectedCompany, columnMapping);
        toast.success(uploadResponse.data.message);
        await markUploadProcessed();
        setStep(4);
      } else {
        // For updates, compare first
        const response = await censusApi.compare(file, selectedCompany, columnMapping);
        setComparison(response.data);
        setStep(3);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const response = await api.post('/census/apply', {
        company_id: selectedCompany,
        new_employees: comparison.new_employees,
        quit_employee_ids: comparison.quit_employees.map(e => e.id),
        address_changed_employees: comparison.address_changed,
        add_new: applyOptions.addNew,
        mark_quit: applyOptions.markQuit,
        update_addresses: applyOptions.updateAddresses
      });
      toast.success(response.data.message);
      await markUploadProcessed();
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply changes');
    } finally {
      setLoading(false);
    }
  };

  const handleWipeCensus = async () => {
    if (!selectedCompany) return;
    const companyName = companies.find(c => c.id === selectedCompany)?.name || 'this company';
    if (!window.confirm(`Are you sure you want to delete ALL employees and upload history for ${companyName}? This cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.delete(`/census/wipe/${selectedCompany}`);
      toast.success(response.data.message);
      loadUploadHistory(selectedCompany);
      checkIfInitialUpload(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to wipe census data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUpload = async (uploadId, addedCount) => {
    const deleteEmployees = addedCount > 0 && window.confirm(
      `This upload added ${addedCount} employees. Do you also want to delete those employees?\n\nClick OK to delete employees too, or Cancel to only delete the upload record.`
    );
    
    if (!window.confirm('Delete this upload record?')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.delete(`/census/upload/${uploadId}`, {
        params: { delete_employees: deleteEmployees }
      });
      toast.success(response.data.message);
      loadUploadHistory(selectedCompany);
      checkIfInitialUpload(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete upload');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setColumnMapping({});
    setComparison(null);
    setProcessingUploadId(null);
    setApplyOptions({ addNew: true, markQuit: true, updateAddresses: true });
    if (selectedCompany) {
      loadUploadHistory(selectedCompany);
      checkIfInitialUpload(selectedCompany);
    }
  };

  const selectedCompanyName = companies.find(c => c.id === selectedCompany)?.name || '';

  return (
    <div className="space-y-6" data-testid="census-upload-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Census Upload</h1>
          <p className="text-muted-foreground mt-1">
            {isInitialUpload ? 'Upload initial employee census data' : 'Update employee census data'}
          </p>
        </div>
        {isInitialUpload && (
          <div className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm font-medium">
            Initial Upload
          </div>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between max-w-2xl">
        {STEPS.map((s, index) => (
          <div key={s.id} className="flex items-center">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step > s.id ? 'bg-green-500 text-white' : step === s.id ? 'bg-[#E31837] text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s.id ? <Check className="w-4 h-4" /> : s.id}
              </div>
              <div className="hidden sm:block">
                <p className={`text-sm font-medium ${step >= s.id ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {s.title}
                </p>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-8 sm:w-16 h-0.5 mx-2 ${step > s.id ? 'bg-green-500' : 'bg-border'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Pending Rep Uploads */}
      {step === 1 && pendingUploads.length > 0 && (
        <Card className="bg-card border-border border-l-4 border-l-yellow-500" data-testid="pending-uploads-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-yellow-500" />
              Files From Company Reps Awaiting Processing ({pendingUploads.length})
            </CardTitle>
            <CardDescription>
              These files were uploaded through the secure public portal. Click Process to run one through the census wizard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {pendingUploads.map((record) => (
                <div key={record.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{record.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {record.company_name} · {new Date(record.timestamp).toLocaleDateString()} {new Date(record.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleProcessPendingUpload(record)}
                    className="bg-[#E31837] hover:bg-[#c41530] text-white shrink-0"
                    data-testid={`process-upload-${record.id}`}
                  >
                    Process <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle>Select Company & Upload File</CardTitle>
                    <CardDescription>
                      Choose the employer company and upload the census file (CSV or Excel)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                        <SelectTrigger className="bg-background border-input max-w-md" data-testid="company-select">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name} ({company.employee_count} employees)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div
                      className={`upload-zone rounded-lg p-8 text-center cursor-pointer ${dragOver ? 'drag-over' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleFileDrop}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-zone"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileDrop}
                        className="hidden"
                      />
                      {file ? (
                        <div className="flex items-center justify-center gap-3">
                          <FileSpreadsheet className="w-10 h-10 text-[#E31837]" />
                          <div className="text-left">
                            <p className="font-medium">{file.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-lg font-medium mb-1">Drop your file here or click to browse</p>
                          <p className="text-sm text-muted-foreground">Supports CSV and Excel files</p>
                        </>
                      )}
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4 text-sm">
                      <p className="font-medium mb-2">Matching Logic:</p>
                      <p className="text-muted-foreground">
                        Employees are matched by <span className="text-foreground font-medium">First Name + Last Name + Start Date</span>. 
                        These fields never change and uniquely identify each employee.
                      </p>
                      <ul className="mt-2 space-y-1 text-muted-foreground">
                        <li>• If someone is not in the new file → marked as <span className="text-red-400">left the company</span></li>
                        <li>• If address changes → status set to <span className="text-yellow-400">pending</span> (need to verify homeowner)</li>
                      </ul>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        onClick={handlePreviewFile}
                        disabled={!file || !selectedCompany || loading}
                        className="bg-[#E31837] hover:bg-[#c41530] text-white"
                        data-testid="next-step-1"
                      >
                        {loading ? <div className="spinner w-4 h-4" /> : (
                          <>Continue <ChevronRight className="w-4 h-4 ml-2" /></>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Upload History */}
              <div>
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Upload History
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {uploadHistory.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No previous uploads</p>
                    ) : (
                      <div className="space-y-3">
                        {uploadHistory.slice(0, 5).map((record) => (
                          <div key={record.id} className="p-3 rounded-lg bg-muted/50 text-sm group relative">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-3 h-3" />
                                {new Date(record.timestamp).toLocaleDateString()} {new Date(record.timestamp).toLocaleTimeString()}
                              </div>
                              <button
                                onClick={() => handleDeleteUpload(record.id, record.added_count)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-500/20 text-red-500"
                                title="Delete this upload"
                                disabled={loading}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex gap-4 text-xs">
                              {record.added_count > 0 && (
                                <span className="text-green-400">+{record.added_count} added</span>
                              )}
                              {record.quit_count > 0 && (
                                <span className="text-red-400">{record.quit_count} left</span>
                              )}
                              {record.address_updated_count > 0 && (
                                <span className="text-yellow-400">{record.address_updated_count} moved</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">by {record.uploaded_by_name || 'Unknown'}</p>
                            {record.file_name && (
                              <div className="flex items-center gap-3 mt-1.5">
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await api.get(`/census/download/${record.id}`, { responseType: 'blob' });
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = record.file_name;
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                    } catch {
                                      toast.error('Failed to download file');
                                    }
                                  }}
                                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                                  data-testid={`download-upload-${record.id}`}
                                >
                                  <Download className="w-3 h-3" />
                                  <span className="truncate max-w-[140px]">{record.file_name}</span>
                                </button>
                                <button
                                  onClick={() => setRemapUploadId(record.id)}
                                  className="flex items-center gap-1 text-xs text-yellow-600 hover:underline"
                                  title="Fix column mapping for this upload"
                                  data-testid={`remap-upload-${record.id}`}
                                >
                                  <Wrench className="w-3 h-3" />
                                  Re-map
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Wipe Census Data Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-red-500 border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
                      onClick={handleWipeCensus}
                      disabled={loading}
                      data-testid="wipe-census-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Wipe All Census Data
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Map Columns */}
          {step === 2 && preview && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Map Columns</CardTitle>
                <CardDescription>
                  Match your file columns. Found {preview.total_rows} rows.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {ALL_FIELDS.map((field) => {
                    const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                    <div key={field} className="space-y-2">
                      <Label className="text-sm">{label}</Label>
                      <Select
                        value={columnMapping[field] || '_none_'}
                        onValueChange={(value) => setColumnMapping({ ...columnMapping, [field]: value === '_none_' ? undefined : value })}
                      >
                        <SelectTrigger className="bg-background border-input" data-testid={`map-${field}`}>
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">None</SelectItem>
                          {preview.columns.map((col) => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    );
                  })}
                </div>

                {/* Preview Table */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium mb-2">Data Preview</h4>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          {preview.columns.slice(0, 8).map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.slice(0, 3).map((row, idx) => (
                          <tr key={idx}>
                            {preview.columns.slice(0, 8).map((col) => (
                              <td key={col} className="text-muted-foreground">
                                {String(row[col] || '').substring(0, 25)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ChevronLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleCompare}
                    disabled={loading}
                    className="bg-[#E31837] hover:bg-[#c41530] text-white"
                    data-testid="next-step-2"
                  >
                    {loading ? <div className="spinner w-4 h-4" /> : (
                      <>{isInitialUpload ? 'Upload Census' : 'Compare Data'} <ChevronRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Review Changes */}
          {step === 3 && comparison && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Review Changes for {selectedCompanyName}</CardTitle>
                <CardDescription>
                  Review the differences between your file and existing data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center gap-3">
                      <UserPlus className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold text-green-500">{comparison.new_employees.length}</p>
                        <p className="text-sm text-muted-foreground">New Employees</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <div className="flex items-center gap-3">
                      <UserMinus className="w-8 h-8 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold text-red-500">{comparison.quit_employees.length}</p>
                        <p className="text-sm text-muted-foreground">Left Company</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-8 h-8 text-yellow-500" />
                      <div>
                        <p className="text-2xl font-bold text-yellow-500">{comparison.address_changed.length}</p>
                        <p className="text-sm text-muted-foreground">Address Changed</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted border border-border">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-muted-foreground" />
                      <div>
                        <p className="text-2xl font-bold">{comparison.unchanged}</p>
                        <p className="text-sm text-muted-foreground">Unchanged</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* New Employees */}
                {comparison.new_employees.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-green-500" />
                      New Employees
                    </h4>
                    <div className="overflow-x-auto rounded-lg border border-border max-h-[200px]">
                      <table className="data-table text-sm">
                        <thead>
                          <tr><th>First Name</th><th>Last Name</th><th>Start Date</th><th>Address</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {comparison.new_employees.map((emp, idx) => (
                            <tr key={idx} className="bg-green-500/5 border-l-2 border-l-green-500">
                              <td className="font-medium">{emp.first_name}</td>
                              <td className="font-medium">{emp.last_name}</td>
                              <td className="text-muted-foreground">{emp.start_date}</td>
                              <td className="text-muted-foreground">{emp.address}, {emp.city}, {emp.state}</td>
                              <td><span className="status-pending px-2 py-0.5 rounded text-xs">{emp.homeowner_status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Quit Employees */}
                {comparison.quit_employees.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <UserMinus className="w-4 h-4 text-red-500" />
                      Employees Who Left (not in new file)
                    </h4>
                    <div className="overflow-x-auto rounded-lg border border-border max-h-[200px]">
                      <table className="data-table text-sm">
                        <thead>
                          <tr><th>First Name</th><th>Last Name</th><th>Start Date</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {comparison.quit_employees.map((emp, idx) => (
                            <tr key={idx} className="bg-red-500/5 border-l-2 border-l-red-500">
                              <td className="font-medium line-through text-muted-foreground">{emp.first_name}</td>
                              <td className="font-medium line-through text-muted-foreground">{emp.last_name}</td>
                              <td className="text-muted-foreground">{emp.start_date}</td>
                              <td><span className={`px-2 py-0.5 rounded text-xs ${emp.homeowner_status === 'homeowner' ? 'status-homeowner' : 'status-renter'}`}>{emp.homeowner_status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Address Changes */}
                {comparison.address_changed.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-yellow-500" />
                      Address Changes (will be set to pending)
                    </h4>
                    <div className="overflow-x-auto rounded-lg border border-border max-h-[200px]">
                      <table className="data-table text-sm">
                        <thead>
                          <tr><th>Name</th><th>Old Address</th><th>New Address</th></tr>
                        </thead>
                        <tbody>
                          {comparison.address_changed.map((emp, idx) => (
                            <tr key={idx} className="bg-yellow-500/5 border-l-2 border-l-yellow-500">
                              <td className="font-medium">{emp.first_name} {emp.last_name}</td>
                              <td className="text-muted-foreground">{emp.old_address}, {emp.old_city}, {emp.old_state}</td>
                              <td className="text-yellow-500">{emp.new_address}, {emp.new_city}, {emp.new_state}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Employees with address changes will have their homeowner status reset to "pending" for re-verification
                    </p>
                  </div>
                )}

                {/* Options */}
                <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                  <h4 className="text-sm font-medium">Apply Options</h4>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="addNew" checked={applyOptions.addNew} onCheckedChange={(checked) => setApplyOptions({ ...applyOptions, addNew: checked })} />
                    <label htmlFor="addNew" className="text-sm">Add {comparison.new_employees.length} new employees</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="markQuit" checked={applyOptions.markQuit} onCheckedChange={(checked) => setApplyOptions({ ...applyOptions, markQuit: checked })} />
                    <label htmlFor="markQuit" className="text-sm">Mark {comparison.quit_employees.length} employees as left</label>
                  </div>
                  {comparison.address_changed.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <Checkbox id="updateAddresses" checked={applyOptions.updateAddresses} onCheckedChange={(checked) => setApplyOptions({ ...applyOptions, updateAddresses: checked })} />
                      <label htmlFor="updateAddresses" className="text-sm">Update {comparison.address_changed.length} address changes (set to pending)</label>
                    </div>
                  )}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ChevronLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={loading}
                    className="bg-[#E31837] hover:bg-[#c41530] text-white"
                    data-testid="apply-changes"
                  >
                    {loading ? <div className="spinner w-4 h-4" /> : (
                      <>Apply Changes <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Complete */}
          {step === 4 && (
            <Card className="bg-card border-border">
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Census Updated Successfully</h2>
                  <p className="text-muted-foreground mb-6">
                    {isInitialUpload 
                      ? 'Initial census data has been uploaded. New employees with address changes will need homeowner verification.'
                      : 'Census has been updated. Employees with address changes are now pending verification.'}
                  </p>
                  <div className="flex justify-center gap-4">
                    <Button variant="outline" onClick={resetWizard} data-testid="upload-another">
                      Upload Another File
                    </Button>
                    <Button
                      className="bg-[#E31837] hover:bg-[#c41530] text-white"
                      onClick={() => window.location.href = '/employees'}
                      data-testid="view-employees"
                    >
                      View Employees
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>

      <RemapDialog
        uploadId={remapUploadId}
        open={!!remapUploadId}
        onOpenChange={(open) => { if (!open) setRemapUploadId(null); }}
        onSuccess={() => loadUploadHistory(selectedCompany)}
      />
    </div>
  );
}
